import { useState, useCallback, useMemo, useRef, useEffect, createContext, useContext } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import { useEmailBuilderStore } from "@/store/emailBuilderStore";
import { blockRegistry } from "@/lib/email-builder/block-registry";
import { BlockIcon } from "./BlockIcon";
import { canAcceptDrop } from "@/lib/email-builder/dnd-validation";
import {
  isSectionTemplate,
  getSectionTemplate,
  getColumnWidth,
} from "@/lib/email-builder/section-templates";
import {
  calculateDropRegions,
  hitTestRegions,
  toMoveIndex,
  type DropRegion,
} from "@/lib/email-builder/drop-region-calculator";
import { HtmlEditorModal } from "./HtmlEditorModal";

// ─── Public types ────────────────────────────────────────────────────

/**
 * Data structure attached to draggable items.
 * Distinguishes between new blocks from palette and existing blocks being moved.
 */
export interface DragData {
  type: "new-block" | "existing-block" | "custom-block";
  blockType?: string;
  blockId?: string;
  customBlockId?: string;
  parentId?: string | null;
  orderIndex?: number;
  imageUrl?: string;
  imageFileName?: string;
}

/**
 * Data structure attached to drop targets (kept for BlockRenderer's useSortable data).
 */
export interface DropData {
  type: "block" | "container" | "empty-zone";
  blockId?: string;
  parentId?: string | null;
  orderIndex?: number;
  acceptsChildren?: boolean;
}

/**
 * The projected insertion point, or null when no valid target is under the pointer.
 */
export interface DropTarget {
  parentId: string | null;
  index: number;
}

/**
 * Shared drag state consumed by containers and block renderers.
 */
export interface DragState {
  activeId: string | null;
  activeData: DragData | null;
  /** Projected drop location — containers read this to render the ghost placeholder. */
  dropTarget: DropTarget | null;
}

export const DragStateContext = createContext<DragState>({
  activeId: null,
  activeData: null,
  dropTarget: null,
});

export function useDragState(): DragState {
  return useContext(DragStateContext);
}

// ─── Component ───────────────────────────────────────────────────────

interface DndProviderProps {
  children: React.ReactNode;
}

/**
 * Drag-and-Drop Context Provider for the Email Builder.
 *
 * On drag start the provider measures every block's DOM rect and builds a
 * static region map. During drag the pointer is hit-tested against those
 * regions (deepest-first) to produce a `dropTarget` that containers read
 * via context to render a ghost placeholder.
 */
export function DndProvider({ children }: DndProviderProps) {
  const addBlock = useEmailBuilderStore((s) => s.addBlock);
  const moveBlockAction = useEmailBuilderStore((s) => s.moveBlockAction);
  const blockTree = useEmailBuilderStore((s) => s.blockTree);
  const setError = useEmailBuilderStore((s) => s.setError);

  const [activeData, setActiveData] = useState<DragData | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [pendingHtmlDrop, setPendingHtmlDrop] = useState<DropTarget | null>(null);

  // Stable ref so callbacks always read the latest tree.
  const blockTreeRef = useRef(blockTree);
  useEffect(() => {
    blockTreeRef.current = blockTree;
  }, [blockTree]);

  // Region map — computed once on drag start, cleared on end/cancel.
  const regionsRef = useRef<DropRegion[]>([]);

  // ── Sensors ────────────────────────────────────────────────────────

  // Pointer only: drop targets come from pointer hit-testing (below), so a keyboard-started drag
  // could never reach a target and would just leave the editor stuck mid-drag.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  // ── Collision detection (no-op) ────────────────────────────────────
  //
  // We no longer use dnd-kit's droppable zones for collision. Pointer
  // tracking is done via a native pointermove listener (below) that
  // hit-tests our static region map.

  const collisionDetection = useCallback(() => {
    return [];
  }, []);

  // ── Pointer tracking → region hit-test ─────────────────────────────

  useEffect(() => {
    if (!activeData) return;

    const onPointerMove = (e: PointerEvent) => {
      const hit = hitTestRegions(regionsRef.current, e.clientX, e.clientY);
      if (hit) {
        setDropTarget(prev => {
          if (prev && prev.parentId === hit.parentId && prev.index === hit.index) {
            return prev; // avoid unnecessary re-renders
          }
          return { parentId: hit.parentId, index: hit.index };
        });
      } else {
        setDropTarget(prev => (prev === null ? prev : null));
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, [activeData]);

  // ── Drag lifecycle ─────────────────────────────────────────────────

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const dragData = event.active.data.current as DragData;

      setActiveData(dragData);
      setActiveId(event.active.id.toString());
      setDropTarget(null);

      // Build the static region map.
      // Use requestAnimationFrame so the DOM has settled (dnd-kit may
      // apply transforms to the active element on the same tick).
      requestAnimationFrame(() => {
        const draggedBlockId =
          dragData.type === "existing-block" ? (dragData.blockId ?? null) : null;

        regionsRef.current = calculateDropRegions(
          blockTreeRef.current,
          dragData,
          draggedBlockId,
        );
      });
    },
    [],
  );

  const handleDragEnd = useCallback(
    async (_event: DragEndEvent) => {
      const data = activeData;
      const target = dropTarget;

      // Clean up first so the UI resets even if the async call fails.
      regionsRef.current = [];
      setActiveData(null);
      setActiveId(null);
      setDropTarget(null);

      if (!data || !target) return;

      // Final validation against current tree
      const validation = canAcceptDrop(data, target, blockTreeRef.current);
      if (!validation.isValid) return;

      try {
        // ── New block from palette ──
        if (data.type === "new-block" && data.blockType) {
          const blockType = data.blockType;

          if (isSectionTemplate(blockType)) {
            const template = getSectionTemplate(blockType);
            if (!template) {
              setError(`Unknown section template: ${blockType}`);
              return;
            }

            const sectionConfig = blockRegistry.get("section");
            const sectionDefaultConfig = sectionConfig?.defaultConfig || {};

            const sectionBlock = await addBlock(
              "section",
              target.parentId ?? undefined,
              target.index,
              sectionDefaultConfig,
            );

            const columnConfig = blockRegistry.get("column");
            const columnDefaultConfig = columnConfig?.defaultConfig || {};

            for (let i = 0; i < template.columnCount; i++) {
              const columnWidth = getColumnWidth(template, i);
              await addBlock("column", sectionBlock.id, i, {
                ...columnDefaultConfig,
                width: columnWidth,
                padding: { top: 0, right: 0, bottom: 0, left: 0 },
              });
            }
          } else if (blockType === "html") {
            // HTML blocks require content up front — open the editor modal
            // instead of calling addBlock with an empty config (which the
            // backend rejects). The modal's onInsert callback finishes the add.
            setPendingHtmlDrop(target);
          } else {
            const bConfig = blockRegistry.get(blockType);
            if (!bConfig) {
              setError(`Unknown block type: ${blockType}`);
              return;
            }

            const defaultConfig: Record<string, unknown> = {
              ...(bConfig.defaultConfig || {}),
            };

            if (blockType === "image" && data.imageUrl) {
              defaultConfig.src = data.imageUrl;
              defaultConfig.alt = data.imageFileName || "Image";
            }

            await addBlock(
              blockType,
              target.parentId ?? undefined,
              target.index,
              defaultConfig,
            );
          }
        }
        // ── Move existing block ──
        else if (data.type === "existing-block" && data.blockId) {
          const normalize = (id: string | null | undefined) => id ?? null;
          const sameParent = normalize(target.parentId) === normalize(data.parentId);
          const currentIndex = data.orderIndex ?? 0;

          if (sameParent && target.index === currentIndex) {
            return; // no-op
          }

          const index = toMoveIndex(target.index, currentIndex, sameParent);

          await moveBlockAction(
            data.blockId,
            target.parentId ?? undefined,
            index,
          );
        }
        // ── Custom block (unsupported) ──
        else if (data.type === "custom-block") {
          setError("Custom blocks feature is not available");
        }
      } catch (error) {
        console.error("[DnD] Error during drop:", error);
      }
    },
    [activeData, dropTarget, addBlock, moveBlockAction, setError],
  );

  const handleDragCancel = useCallback(() => {
    regionsRef.current = [];
    setActiveData(null);
    setActiveId(null);
    setDropTarget(null);
  }, []);

  // ── Context value ──────────────────────────────────────────────────

  const dragStateValue = useMemo<DragState>(
    () => ({ activeId, activeData, dropTarget }),
    [activeId, activeData, dropTarget],
  );

  // ── Render ─────────────────────────────────────────────────────────

  const handleHtmlInsert = useCallback(
    async (content: string) => {
      const target = pendingHtmlDrop;
      setPendingHtmlDrop(null);
      if (!target) return;

      const bConfig = blockRegistry.get("html");
      const defaultConfig: Record<string, unknown> = {
        ...(bConfig?.defaultConfig || {}),
        content,
      };

      try {
        await addBlock("html", target.parentId ?? undefined, target.index, defaultConfig);
      } catch (error) {
        console.error("[DnD] Error adding HTML block:", error);
      }
    },
    [pendingHtmlDrop, addBlock],
  );

  const handleHtmlCancel = useCallback(() => {
    setPendingHtmlDrop(null);
  }, []);

  return (
    <DragStateContext.Provider value={dragStateValue}>
      <HtmlEditorModal
        isOpen={pendingHtmlDrop !== null}
        onInsert={handleHtmlInsert}
        onCancel={handleHtmlCancel}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}

        <DragOverlay
          dropAnimation={{
            sideEffects: defaultDropAnimationSideEffects({
              styles: { active: { opacity: "0.5" } },
            }),
          }}
          modifiers={[
            ({ transform }) => ({
              ...transform,
              x: transform.x + 10,
              y: transform.y + 10,
            }),
          ]}
        >
          {activeData && renderDragOverlay(activeData)}
        </DragOverlay>
      </DndContext>
    </DragStateContext.Provider>
  );
}

// ─── Drag overlay ────────────────────────────────────────────────────

function renderDragOverlay(activeData: DragData) {
  if (activeData.type === "new-block" && activeData.blockType) {
    if (activeData.imageUrl) {
      return (
        <div className="rounded-md border-2 border-primary bg-card shadow-lg opacity-90 overflow-hidden w-32">
          <div className="relative w-full h-20">
            <img
              src={activeData.imageUrl}
              alt={activeData.imageFileName || "Image"}
              className="object-cover w-full h-full"
            />
          </div>
          <div className="p-1.5 text-xs font-medium text-card-foreground truncate">
            {activeData.imageFileName || "Image"}
          </div>
        </div>
      );
    }

    const blockConfig = blockRegistry.get(activeData.blockType);
    if (!blockConfig) {
      return (
        <div className="rounded-md border-2 border-primary bg-background p-3 shadow-lg">
          <div className="text-sm font-medium text-foreground">Unknown Block</div>
        </div>
      );
    }

    return (
      <div className="rounded-md border-2 border-primary bg-card p-3 shadow-lg opacity-90">
        <div className="flex items-center gap-2">
          {blockConfig.icon && (
            <div className="text-muted-foreground">
              <BlockIcon iconName={blockConfig.icon} />
            </div>
          )}
          <span className="text-sm font-medium text-card-foreground">
            {blockConfig.displayName}
          </span>
        </div>
      </div>
    );
  }

  if (activeData.type === "existing-block") {
    // Ghost placeholder in the canvas provides the main feedback.
    return null;
  }

  if (activeData.type === "custom-block") {
    return (
      <div className="rounded-md border-2 border-primary bg-card p-3 shadow-lg opacity-90">
        <div className="flex items-center gap-2">
          <svg
            className="h-4 w-4 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 10h16M4 14h16M4 18h16"
            />
          </svg>
          <span className="text-sm font-medium text-card-foreground">
            Custom Block
          </span>
        </div>
      </div>
    );
  }

  return null;
}
